"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import {
  AUDIT_CHOOSER_OPTIONS,
  HOMEPAGE_URL_LABEL,
  HOMEPAGE_URL_PLACEHOLDER,
  type AuditChooserId,
} from "~/lib/siteglue/landing-redesign/copy";
import { trackLandingEvent } from "~/lib/siteglue/landing-redesign/events";
import {
  normalizeBusinessWebsite,
  persistLandingUrl,
  URL_HELPER_INVALID,
} from "~/lib/siteglue/landing-redesign/url";

function hrefForAudit(id: AuditChooserId, website?: string): string {
  const href =
    AUDIT_CHOOSER_OPTIONS.find((option) => option.id === id)?.href ?? AUDIT_CHOOSER_OPTIONS[0].href;
  if (!website) return href;
  return `${href}?url=${encodeURIComponent(website)}`;
}

export function AuditChooserForm({
  location = "homepage_hero",
}: {
  location?: "homepage_hero" | "homepage_final" | "how_it_works";
} = {}) {
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const go = (id: AuditChooserId) => {
    const trimmed = url.trim();
    let website: string | undefined;
    if (trimmed) {
      const normalized = normalizeBusinessWebsite(trimmed);
      if (!normalized.ok) {
        setError(URL_HELPER_INVALID);
        return;
      }
      website = normalized.href;
      persistLandingUrl(website);
    }
    setError(null);
    const href = hrefForAudit(id, website);
    trackLandingEvent("url_submit", { url_source: location, audit: id });
    trackSiteGlueEvent("homepage_primary_cta_click", { destination: href, audit: id });
    router.push(href);
  };

  return (
    <div className="w-full max-w-2xl space-y-3">
      <label className="block text-sm font-semibold text-slate-800" htmlFor={`${location}-url`}>
        {HOMEPAGE_URL_LABEL}
      </label>
      <input
        id={`${location}-url`}
        type="text"
        inputMode="url"
        autoComplete="url"
        placeholder={HOMEPAGE_URL_PLACEHOLDER}
        value={url}
        onChange={(event) => {
          setUrl(event.target.value);
          if (error) setError(null);
        }}
        className="h-12 w-full rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900"
      />
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <div className="grid gap-2 sm:grid-cols-3">
        {AUDIT_CHOOSER_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => go(option.id)}
            className="inline-flex h-12 items-center justify-center rounded-full bg-[#4f81bd] px-4 text-sm font-semibold text-white hover:opacity-95"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
